import React, { useState } from 'react';
import './App.css';
import './index.css';

import Titlebar from './Titlebar';
import AdminProfileTable from './AdminProfileTable';
import { apiUrl } from './config';

const admin = {
    'standard_rate': 15.00,
}

function AdminForm() {
    const [csvFile, setCsvFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    // Profile management
    const [profileEmail, setProfileEmail] = useState('');
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState(null);

    const handleProfileSearch = async (e) => {
        e.preventDefault();
        if (!profileEmail.trim()) return;
        setProfileLoading(true);
        setProfileError(null);
        setSelectedProfile(null);
        try {
            const response = await fetch(
              apiUrl(`/profile/email?email=${encodeURIComponent(profileEmail.trim())}`)
            );
            if (response.ok) {
                const data = await response.json();
                setSelectedProfile(data);
            } else if (response.status === 404) {
                setProfileError('No profile found for that email address.');
            } else {
                setProfileError('Failed to load profile.');
            }
        } catch (err) {
            setProfileError('Error fetching profile: ' + err.message);
        } finally {
            setProfileLoading(false);
        }
    };

    const handleProfileSave = async (updatedProfile) => {
        const response = await fetch(
        apiUrl('/profile/save'),
            {
          method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedProfile),
            }
        );
        if (!response.ok) {
            throw new Error('Failed to save profile changes.');
        }
        const saved = await response.json();
        setSelectedProfile(saved);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'text/csv') {
            setCsvFile(file);
        } else {
            alert('Please select a valid CSV file');
            e.target.value = '';
        }
    };

    const handleCsvUpload = async (e) => {
        e.preventDefault();
        if (!csvFile) {
            alert('Please select a CSV file first');
            return;
        }

        setIsUploading(true);
        
        try {
            const formData = new FormData();
            formData.append('csv_file', csvFile);

            const response = await fetch(apiUrl('/customer/import'), {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const result = await response.json();
                alert(`Successfully imported ${result.imported || 0} customers`);
                setCsvFile(null);
                // Reset file input
                document.getElementById('csv_file').value = '';
            } else {
                alert('Failed to import customers');
            }
        } catch (error) {
            console.error('Error importing customers:', error);
            alert('Error importing customers');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
            <Titlebar title="Admin Settings"/>

        <div className="m-3">



          {/* Customer Import Section */}
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">Customer Import</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">Upload a CSV file to import customers in bulk.</p>

            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="col-span-full">
                <label htmlFor="csv_file" className="block text-sm font-medium leading-6 text-gray-900">Customer CSV File</label>
                <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-300" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M11.47 2.47a.75.75 0 011.06 0l4.5 4.5a.75.75 0 01-1.06 1.06l-3.22-3.22V16.5a.75.75 0 01-1.5 0V4.81L8.03 8.03a.75.75 0 01-1.06-1.06l4.5-4.5zM3 15.75a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                    </svg>
                    <div className="mt-4 flex text-sm leading-6 text-gray-600">
                      <label htmlFor="csv_file" className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500">
                        <span>Upload CSV file</span>
                        <input 
                          id="csv_file" 
                          name="csv_file" 
                          type="file" 
                          accept=".csv"
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs leading-5 text-gray-600">CSV files only</p>
                    {csvFile && (
                      <p className="mt-2 text-sm text-green-600">
                        Selected: {csvFile.name}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Import Button - Now more visible */}
                <div className="mt-6 flex gap-4">
                  <button
                    type="button"
                    onClick={handleCsvUpload}
                    disabled={!csvFile || isUploading}
                    className="inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {isUploading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Importing...
                      </>
                    ) : (
                      'Import Customers'
                    )}
                  </button>
                  
                  {/* Clear selection button */}
                  {csvFile && !isUploading && (
                    <button
                      type="button"
                      onClick={() => {
                        setCsvFile(null);
                        document.getElementById('csv_file').value = '';
                      }}
                      className="inline-flex items-center rounded-md bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500"
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
                
                <div className="mt-4 text-sm text-gray-600">
                  <p className="font-medium">CSV Format Requirements:</p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>Headers: name, billing_name, account_rep, address_1, address_2, city, state, zip_code, telephone, contact_name, contact_email, contact_phone</li>
                    <li>Optional billing fields: billing_address_1, billing_address_2, billing_city, billing_state, billing_zip_code, billing_contact_name, billing_contact_email, billing_contact_phone</li>
                    <li>First row should contain column headers</li>
                    <li>UTF-8 encoding recommended</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* System Settings Section */}
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">System Settings</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">Configure system-wide settings and rates.</p>

            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-4">
                <label htmlFor="standard_rate" className="block text-sm font-medium leading-6 text-gray-900">Standard Rate</label>
                <div className="mt-2">
                  <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                    <span className="flex select-none items-center pl-3 text-gray-500 sm:text-sm">$</span>
                    <input type="number" name="standard_rate" id="standard_rate" step="0.01" className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="15.00" defaultValue={admin.standard_rate}/>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rest of the form sections remain the same... */}
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">Personal Information</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">Use a permanent address where you can receive mail.</p>
      
            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label htmlFor="first-name" className="block text-sm font-medium leading-6 text-gray-900">First name</label>
                <div className="mt-2">
                  <input type="text" name="first-name" id="first-name" autoComplete="given-name" className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"/>
                </div>
              </div>
      
              <div className="sm:col-span-3">
                <label htmlFor="last-name" className="block text-sm font-medium leading-6 text-gray-900">Last name</label>
                <div className="mt-2">
                  <input type="text" name="last-name" id="last-name" autoComplete="family-name" className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"/>
                </div>
              </div>
      
              <div className="sm:col-span-4">
                <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">Email address</label>
                <div className="mt-2">
                  <input id="email" name="email" type="email" autoComplete="email" className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"/>
                </div>
              </div>
            </div>
          </div>

          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">Notifications</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">We'll always let you know about important changes, but you pick what else you want to hear about.</p>
      
            <div className="mt-10 space-y-10">
              <fieldset>
                <legend className="text-sm font-semibold leading-6 text-gray-900">By Email</legend>
                <div className="mt-6 space-y-6">
                  <div className="relative flex gap-x-3">
                    <div className="flex h-6 items-center">
                      <input id="comments" name="comments" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"/>
                    </div>
                    <div className="text-sm leading-6">
                      <label htmlFor="comments" className="font-medium text-gray-900">Comments</label>
                      <p className="text-gray-500">Get notified when someones posts a comment on a posting.</p>
                    </div>
                  </div>
                  <div className="relative flex gap-x-3">
                    <div className="flex h-6 items-center">
                      <input id="candidates" name="candidates" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"/>
                    </div>
                    <div className="text-sm leading-6">
                      <label htmlFor="candidates" className="font-medium text-gray-900">Candidates</label>
                      <p className="text-gray-500">Get notified when a candidate applies for a job.</p>
                    </div>
                  </div>
                  <div className="relative flex gap-x-3">
                    <div className="flex h-6 items-center">
                      <input id="offers" name="offers" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"/>
                    </div>
                    <div className="text-sm leading-6">
                      <label htmlFor="offers" className="font-medium text-gray-900">Offers</label>
                      <p className="text-gray-500">Get notified when a candidate accepts or rejects an offer.</p>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      
          {/* User Profile Management Section */}
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">User Profile Management</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">Look up a user by email to view and manage their profile, roles, and functional areas.</p>

            <form onSubmit={handleProfileSearch} className="mt-6 flex items-end gap-3">
              <div className="flex-1 sm:max-w-sm">
                <label htmlFor="profile-email" className="block text-sm font-medium leading-6 text-gray-900">Email address</label>
                <div className="mt-2">
                  <input
                    id="profile-email"
                    type="email"
                    value={profileEmail}
                    onChange={(e) => setProfileEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={!profileEmail.trim() || profileLoading}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-40"
              >
                {profileLoading ? 'Loading…' : 'Look Up'}
              </button>
            </form>

            {profileError && (
              <p className="mt-3 text-sm text-red-600">{profileError}</p>
            )}

            {selectedProfile && (
              <div className="mt-6">
                <AdminProfileTable
                  profile={selectedProfile}
                  onSave={handleProfileSave}
                />
              </div>
            )}
          </div>

        <div className="mt-6 flex items-center justify-end gap-x-6">
          <button type="button" className="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
          <button type="submit" className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Save</button>
        </div>
        </div>
    );
}

export default AdminForm;